// Property `attributes` JSONB render model (PKT-0246).
//
// We enriched 202 properties into a `properties.attributes` JSONB and it's
// already on the /enriched wire — but the Properties tab renders almost none of
// it. This module turns the raw blob into clean, sectioned, human-labeled
// signals the UI can render directly.
//
// The live shape (verified against prod 2026-07-12) is NOT uniformly boolean:
//   - most keys are the string enum "yes" | "no" | "unknown"
//   - some keys carry richer enum VALUES: flooring_primary="wood_style_lvp",
//     view="skyline", countertops="granite", building_type="high_rise", …
//   - numeric keys: walk_score, stories (often null)
//   - nearby_anchors: string[]
//   - amenities: a sub-object of 23 { key: "yes"|"no"|"unknown" }
//   - _scope: a top-level sub-object { key: "all"|"select" } — "select" ⇒ the
//     feature is in select units only (render a qualifier, not a building-wide yes)
//   - _evidence: a top-level sub-object { key: "<verbatim site quote>" }
//     (amenity keys are dotted, e.g. "amenities.pool") — used for tooltips
//
// Two guardrails the adversarial pass flagged and this module enforces:
//   1. TYPE-GUARD: `attributes` is sometimes missing / list-typed — never touch
//      keys without confirming it's a plain object first.
//   2. VALUE-DOMAIN: a `=== true` guard drops the majority of real signal.
//      Enum keys must render their VALUE as the label; yes/no/unknown keys
//      render only on an affirmative value; "unknown"/"no"/"none"/null render
//      nothing (never a false "No X").

export type RawAttributes = Record<string, unknown>;

export interface AttributeSignal {
  key: string;
  label: string;
  /** "select" when this feature is only in select units (from _scope). */
  scope?: "all" | "select";
  /** Verbatim site quote (from _evidence), if present — for a tooltip. */
  evidence?: string;
}

export interface AttributeSection {
  key: SectionKey;
  title: string;
  signals: AttributeSignal[];
}

export type SectionKey =
  | "location"
  | "amenities"
  | "finishes"
  | "building"
  | "parking";

// ── value helpers ────────────────────────────────────────────────────────────

// Affirmative values for a yes/no key (prod stores strings; tolerate booleans).
const AFFIRMATIVE = new Set(["yes", "true", "included", "select"]);
// Values that mean "no signal" — render nothing.
const EMPTY_VALUES = new Set(["", "no", "none", "unknown", "null", "false", "0"]);

function asScalar(v: unknown): string | number | null {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? "true" : "false";
  if (typeof v === "string") return v.trim();
  return null;
}

function isMeaningful(v: unknown): boolean {
  const s = asScalar(v);
  if (s === null) return false;
  if (typeof s === "number") return Number.isFinite(s) && s > 0;
  return s !== "" && !EMPTY_VALUES.has(s.toLowerCase());
}

/** Type-guard: a usable attributes object (not missing / array / scalar). */
export function isAttributeObject(attributes: unknown): attributes is RawAttributes {
  return (
    !!attributes &&
    typeof attributes === "object" &&
    !Array.isArray(attributes)
  );
}

// ── key registries ───────────────────────────────────────────────────────────

type Kind = "yesno" | "enum" | "numeric";

interface KeyConfig {
  label?: string; // for yesno / numeric
  kind: Kind;
  section: SectionKey;
  values?: Record<string, string>; // enum value → label
  /** numeric rendering: "Walk Score 88" (label-first, default) vs "25 stories" (value-first). */
  numericStyle?: "label-first" | "value-first";
}

// Generic yes/no/scope tokens — an enum key that lands on one of these (rather
// than a real descriptor) carries no renderable label; skip it instead of
// emitting a bare "Yes" chip.
const GENERIC_TOKENS = new Set([
  "yes", "no", "true", "false", "select", "all", "included", "none", "unknown",
]);

// Enum value → human label maps.
const V_FLOORING: Record<string, string> = {
  hardwood: "Hardwood floors",
  wood_style_lvp: "Wood-style plank floors",
  tile: "Tile floors",
  carpet: "Carpeted",
  mixed: "Mixed flooring",
};
const V_WINDOWS: Record<string, string> = {
  floor_to_ceiling: "Floor-to-ceiling windows",
  oversized: "Oversized windows",
  standard: "Standard windows",
};
const V_CEILING: Record<string, string> = {
  "9ft": "9ft ceilings",
  "10ft_plus": "10ft+ ceilings",
  vaulted: "Vaulted ceilings",
  exposed_loft: "Exposed-loft ceilings",
  standard: "Standard ceilings",
};
const V_COUNTERS: Record<string, string> = {
  quartz: "Quartz counters",
  granite: "Granite counters",
  marble: "Marble counters",
  laminate: "Laminate counters",
  mixed: "Mixed counters",
};
const V_CABINETRY: Record<string, string> = {
  shaker: "Shaker cabinets",
  european_flat: "Euro flat-panel cabinets",
  two_tone: "Two-tone cabinets",
  standard: "Standard cabinets",
};
const V_LAUNDRY: Record<string, string> = {
  included: "In-unit washer/dryer",
  connections: "Washer/dryer connections",
};
const V_OUTDOOR: Record<string, string> = {
  balcony: "Balcony",
  patio: "Patio",
  yard: "Private yard",
};
const V_VIEW: Record<string, string> = {
  skyline: "Skyline view",
  park: "Park view",
  city: "City view",
  courtyard: "Courtyard view",
};
const V_CARPET_FREE: Record<string, string> = {
  yes: "Carpet-free",
  bedrooms_carpeted: "Carpet-free living areas",
};
const V_BUILDING: Record<string, string> = {
  high_rise: "High-rise",
  mid_rise: "Mid-rise",
  garden: "Garden-style",
  loft: "Loft building",
  townhome: "Townhome",
  student: "Student housing",
};
const V_RENOVATION: Record<string, string> = {
  renovated: "Renovated",
  classic: "Classic finishes",
  mixed: "Mixed vintages",
  new_construction: "New construction",
};
const V_PARKING_TYPE: Record<string, string> = {
  garage: "Garage parking",
  deck: "Deck parking",
  surface: "Surface parking",
};

// The full top-level key registry (excludes amenities/_scope/_evidence, handled separately).
const KEYS: Record<string, KeyConfig> = {
  // Finishes / in-unit
  flooring_primary: { kind: "enum", section: "finishes", values: V_FLOORING },
  carpet_free: { kind: "enum", section: "finishes", values: V_CARPET_FREE },
  windows: { kind: "enum", section: "finishes", values: V_WINDOWS },
  ceiling: { kind: "enum", section: "finishes", values: V_CEILING },
  countertops: { kind: "enum", section: "finishes", values: V_COUNTERS },
  cabinetry: { kind: "enum", section: "finishes", values: V_CABINETRY },
  appliances_stainless: { kind: "yesno", section: "finishes", label: "Stainless appliances" },
  gas_range: { kind: "yesno", section: "finishes", label: "Gas range" },
  kitchen_island: { kind: "yesno", section: "finishes", label: "Kitchen island" },
  soaking_tub: { kind: "yesno", section: "finishes", label: "Soaking tub" },
  frameless_glass_shower: { kind: "yesno", section: "finishes", label: "Frameless glass shower" },
  double_vanity: { kind: "yesno", section: "finishes", label: "Double vanity" },
  walk_in_closet: { kind: "yesno", section: "finishes", label: "Walk-in closet" },
  in_unit_storage: { kind: "yesno", section: "finishes", label: "In-unit storage" },
  in_unit_laundry: { kind: "enum", section: "finishes", values: V_LAUNDRY },
  smart_home: { kind: "yesno", section: "finishes", label: "Smart-home features" },
  private_outdoor: { kind: "enum", section: "finishes", values: V_OUTDOOR },
  view: { kind: "enum", section: "finishes", values: V_VIEW },
  furnished_option: { kind: "yesno", section: "finishes", label: "Furnished option" },
  // Building
  building_type: { kind: "enum", section: "building", values: V_BUILDING },
  stories: { kind: "numeric", section: "building", label: "stories", numericStyle: "value-first" },
  loft_style: { kind: "yesno", section: "building", label: "Loft-style" },
  elevator: { kind: "yesno", section: "building", label: "Elevator" },
  gated: { kind: "yesno", section: "building", label: "Gated / controlled access" },
  smoke_free: { kind: "yesno", section: "building", label: "Smoke-free" },
  renovation_tier: { kind: "enum", section: "building", values: V_RENOVATION },
  accessible_units: { kind: "yesno", section: "building", label: "Accessible units" },
  // Parking
  parking_type: { kind: "enum", section: "parking", values: V_PARKING_TYPE },
  parking_assigned: { kind: "yesno", section: "parking", label: "Assigned parking" },
  parking_covered: { kind: "yesno", section: "parking", label: "Covered parking" },
  parking_ev: { kind: "yesno", section: "parking", label: "EV charging" },
  // Location
  near_marta: { kind: "yesno", section: "location", label: "Near MARTA" },
  near_beltline: { kind: "yesno", section: "location", label: "Near the BeltLine" },
  walk_score: { kind: "numeric", section: "location", label: "Walk Score" },
};

export const AMENITY_LABELS: Record<string, string> = {
  pool: "Pool",
  rooftop_pool: "Rooftop pool",
  fitness_center: "Fitness center",
  fitness_24hr: "24hr fitness",
  yoga_studio: "Yoga studio",
  rooftop_lounge: "Rooftop lounge",
  dog_park: "Dog park",
  pet_spa: "Pet spa",
  coworking: "Coworking space",
  clubroom: "Resident clubroom",
  grill_outdoor_kitchen: "Outdoor kitchen / grills",
  fire_pit: "Fire pit",
  ev_charging: "EV charging",
  concierge: "Concierge",
  package_lockers: "Package lockers",
  bike_storage: "Bike storage",
  game_room: "Game room",
  theater: "Theater room",
  golf_sim: "Golf simulator",
  guest_suite: "Guest suite",
  business_center: "Business center",
  sauna: "Sauna",
  spa: "Spa",
};

const SECTION_TITLES: Record<SectionKey, string> = {
  location: "Location intel",
  amenities: "Community amenities",
  finishes: "Interior & finishes",
  building: "Building profile",
  parking: "Parking",
};

// ── scope + evidence lookups ─────────────────────────────────────────────────

function scopeFor(attributes: RawAttributes, key: string): "all" | "select" | undefined {
  const scope = attributes["_scope"];
  if (!isAttributeObject(scope)) return undefined;
  const v = asScalar(scope[key]);
  return v === "select" ? "select" : v === "all" ? "all" : undefined;
}

function evidenceFor(attributes: RawAttributes, key: string): string | undefined {
  const ev = attributes["_evidence"];
  if (!isAttributeObject(ev)) return undefined;
  const v = ev[key];
  return typeof v === "string" && v.trim() ? v.trim() : undefined;
}

// Build a single signal for a top-level key, or null if it carries no signal.
function signalForKey(attributes: RawAttributes, key: string): AttributeSignal | null {
  const cfg = KEYS[key];
  if (!cfg) return null;
  const raw = attributes[key];
  if (!isMeaningful(raw)) return null;

  let label: string | null = null;
  if (cfg.kind === "enum") {
    const val = String(asScalar(raw)).toLowerCase();
    const mapped = cfg.values?.[val];
    if (mapped) {
      label = mapped;
    } else if (GENERIC_TOKENS.has(val)) {
      // A yes/no-style value on an enum key carries no descriptor — don't emit
      // a bare "Yes" chip (the value-domain map's whole point is the value IS
      // the label). Drop it rather than mislabel.
      return null;
    } else {
      // A real, unmapped descriptor value → prettify rather than lose signal.
      label = prettifyValue(val);
    }
  } else if (cfg.kind === "numeric") {
    const v = asScalar(raw);
    label = cfg.numericStyle === "value-first" ? `${v} ${cfg.label}` : `${cfg.label} ${v}`;
  } else {
    // Meaningful text can still be uncertain; use the same domain as amenities.
    if (!AFFIRMATIVE.has(String(asScalar(raw)).toLowerCase())) return null;
    label = cfg.label ?? prettifyValue(key);
  }
  if (!label) return null;

  return {
    key,
    label,
    scope: scopeFor(attributes, key) ?? (String(asScalar(raw)).toLowerCase() === "select" ? "select" : undefined),
    evidence: evidenceFor(attributes, key),
  };
}

function prettifyValue(v: string): string {
  return v
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// Amenity signals from the sub-object.
function amenitySignals(attributes: RawAttributes): AttributeSignal[] {
  const amenities = attributes["amenities"];
  if (!isAttributeObject(amenities)) return [];
  const out: AttributeSignal[] = [];
  for (const [key, val] of Object.entries(amenities)) {
    const s = asScalar(val);
    if (s === null) continue;
    if (!AFFIRMATIVE.has(String(s).toLowerCase())) continue;
    const dotted = `amenities.${key}`;
    out.push({
      key: dotted,
      label: AMENITY_LABELS[key] ?? prettifyValue(key),
      // _scope can carry dotted amenity keys ({"amenities.pool":"select"}) — honor them.
      scope: scopeFor(attributes, dotted),
      evidence: evidenceFor(attributes, dotted),
    });
  }
  return out;
}

// nearby_anchors → signals (location).
function anchorSignals(attributes: RawAttributes): AttributeSignal[] {
  const anchors = attributes["nearby_anchors"];
  if (!Array.isArray(anchors)) return [];
  return anchors
    .map((a) => (typeof a === "string" ? a.trim() : ""))
    .filter(Boolean)
    .map((name) => ({ key: `anchor:${name}`, label: name }));
}

// ── public API ───────────────────────────────────────────────────────────────

/**
 * Structured, sectioned signals for the detail view. Empty sections are
 * omitted. Safe on any input (missing / non-object → []).
 */
export function getStructuredAttributes(attributes: unknown): AttributeSection[] {
  if (!isAttributeObject(attributes)) return [];

  const bySection: Record<SectionKey, AttributeSignal[]> = {
    location: [],
    amenities: [],
    finishes: [],
    building: [],
    parking: [],
  };

  for (const key of Object.keys(KEYS)) {
    const sig = signalForKey(attributes, key);
    if (sig) bySection[KEYS[key].section].push(sig);
  }
  bySection.location.push(...anchorSignals(attributes));
  bySection.amenities.push(...amenitySignals(attributes));

  const order: SectionKey[] = ["location", "amenities", "finishes", "building", "parking"];

  // De-dupe by normalized label across sections so a fact carried by two keys
  // (e.g. parking_ev + amenities.ev_charging both → "EV charging") shows once.
  // First occurrence in section order wins (amenities before parking).
  const seenLabels = new Set<string>();
  return order
    .filter((s) => bySection[s].length > 0)
    .map((s) => ({
      key: s,
      title: SECTION_TITLES[s],
      signals: bySection[s].filter((sig) => {
        const norm = sig.label.toLowerCase();
        if (seenLabels.has(norm)) return false;
        seenLabels.add(norm);
        return true;
      }),
    }))
    .filter((sec) => sec.signals.length > 0);
}

// Fixed-priority signal ids for the list-card "signal strip" (cards align).
const STRIP_PRIORITY: string[] = [
  "near_marta",
  "near_beltline",
  "walk_score",
  "amenities.pool",
  "amenities.rooftop_pool",
  "amenities.fitness_center",
  "amenities.fitness_24hr",
  "view",
  "in_unit_laundry",
  "walk_in_closet",
  "amenities.dog_park",
  "amenities.ev_charging",
  "gated",
  "renovation_tier",
];

/**
 * Top signals for the list card, in a fixed priority order so cards align.
 * Returns compact labels. Safe on any input.
 */
export function getSignalStripSignals(
  attributes: unknown,
  limit = 6
): AttributeSignal[] {
  const sections = getStructuredAttributes(attributes);
  const byKey = new Map<string, AttributeSignal>();
  for (const sec of sections) for (const sig of sec.signals) byKey.set(sig.key, sig);

  const picked: AttributeSignal[] = [];
  for (const key of STRIP_PRIORITY) {
    const sig = byKey.get(key);
    if (sig) {
      picked.push(sig);
      byKey.delete(key);
    }
    if (picked.length >= limit) return picked;
  }
  // Backfill with any remaining signals (amenities first feel) if under limit.
  for (const sig of byKey.values()) {
    picked.push(sig);
    if (picked.length >= limit) break;
  }
  return picked;
}

/** Total meaningful signal count — for a "+N more" affordance / empty-state. */
export function countAttributeSignals(attributes: unknown): number {
  return getStructuredAttributes(attributes).reduce(
    (n, s) => n + s.signals.length,
    0
  );
}
